import { QueryClient } from '@tanstack/react-query'
import { configService } from '@/config'
import { log } from '@/utils/logger'
import { apiService } from '@/services/api'

/**
 * Frontend Cache Warmer
 *
 * Pre-fetches critical data on application startup to ensure:
 * - Instant dashboard loads (cache pre-populated)
 * - No loading spinners on first visit
 * - Better first impression for users
 *
 * This runs asynchronously during app initialization and does not block rendering.
 */

/**
 * Warm up the frontend cache with critical data
 * Called during app initialization in main.jsx
 */
export const warmFrontendCache = async (queryClient: QueryClient): Promise<void> => {
  const startTime = Date.now()
  log.info('Starting frontend cache warm-up...')

  try {
    // Don't wait for API service initialization - start prefetching immediately
    // The API service will use smart defaults and cache warming shouldn't be blocked
    const network = configService.getCurrentNetwork()

    // Prefetch critical data in parallel for faster warm-up
    const warmUpPromises = [
      // Epoch info - needed for staking pages (using working endpoint)
      queryClient.prefetchQuery({
        queryKey: ['epoch-info', network],
        queryFn: async () => {
          const url = apiService.buildUrl(`/api/rpc/epoch-info?network=${network}`)
          const response = await fetch(url)
          if (!response.ok) throw new Error('Failed to fetch epoch info')
          return response.json()
        },
        staleTime: 60 * 1000, // 1 minute
      }),

      // Network health (RPC-backed, computed on-demand)
      queryClient.prefetchQuery({
        queryKey: ['network-health'],
        queryFn: async () => {
          const response = await fetch(`/api/network-health`)
          if (!response.ok) throw new Error('Failed to fetch network health')
          return response.json()
        },
        staleTime: 60 * 1000,
      }),

      // TPS dashboard from CH-backed endpoint (migrations 026+027). Replaces
      // /api/data/tps-dashboard.json (legacy vite middleware file route).
      queryClient.prefetchQuery({
        queryKey: ['tps-dashboard'],
        queryFn: async () => {
          const response = await fetch(`/api/network/tps-dashboard?network=mainnet&window=1h`)
          if (!response.ok) throw new Error('Failed to fetch TPS dashboard')
          return response.json()
        },
        staleTime: 15 * 1000,
      }),

      // Prefetch validators (slowest dashboard query)
      queryClient.prefetchQuery({
        queryKey: ['validators', network],
        queryFn: async () => {
          const module = await import('@/services/centralizedValidatorService')
          return module.default.getAllValidators()
        },
        staleTime: 3 * 60 * 1000, // 3 minutes - match Dashboard
      }),

      // Prefetch vote accounts
      queryClient.prefetchQuery({
        queryKey: ['vote-accounts', network],
        queryFn: async () => {
          const response = await fetch(`/api/rpc/vote-accounts?network=${network}`)
          if (!response.ok) throw new Error('Failed to fetch vote accounts')
          return response.json()
        },
        staleTime: 30 * 1000, // 30 seconds
      }),

      // Prefetch cluster nodes (extract .nodes to match Validators page expectation)
      queryClient.prefetchQuery({
        queryKey: ['cluster-nodes', network],
        queryFn: async () => {
          const response = await fetch(`/api/rpc/cluster-nodes?network=${network}`)
          if (!response.ok) throw new Error('Failed to fetch cluster nodes')
          const data = await response.json()
          return data.nodes || [] // Extract nodes array to match Validators page format
        },
        staleTime: 2 * 60 * 1000, // 2 minutes
      }),

      // Prefetch staking yield (Network Stake card)
      queryClient.prefetchQuery({
        queryKey: ['staking-yield', network],
        queryFn: async () => {
          const response = await fetch(`/api/staking/yield?network=${network}`)
          if (!response.ok) throw new Error('Failed to fetch staking yield')
          return response.json()
        },
        staleTime: 5 * 60 * 1000, // 5 minutes - match SolanaBeachHeader
      }),

      // Prefetch all validators stake breakdown (for instant validator details loading)
      queryClient.prefetchQuery({
        queryKey: ['all-validator-stakes', network],
        queryFn: async () => {
          const response = await fetch(`/api/validators/all-stakes?network=${network}`)
          if (!response.ok) throw new Error('Failed to fetch all stakes')
          return response.json()
        },
        staleTime: 60 * 1000, // 1 minute
      }),
    ]

    // Wait for all prefetch operations to complete
    await Promise.allSettled(warmUpPromises)

    const duration = Date.now() - startTime
    log.info(`Frontend cache warm-up completed in ${duration}ms`)
  } catch (error) {
    log.warn('Frontend cache warm-up failed:', error)
    // Don't throw - cache warming is optional and shouldn't break the app
  }
}

/**
 * Warm up cache for a specific network
 * Useful when switching networks to pre-populate new network data
 */
export const warmCacheForNetwork = async (
  queryClient: QueryClient,
  network: string
): Promise<void> => {
  log.info(`Warming cache for network: ${network}`)

  try {
    // Don't wait for API service initialization - start prefetching immediately
    await Promise.allSettled([
      queryClient.prefetchQuery({
        queryKey: ['epoch-info', network],
        queryFn: async () => {
          const url = apiService.buildUrl(`/api/rpc/epoch-info?network=${network}`)
          const response = await fetch(url)
          return response.json()
        },
      }),
      queryClient.prefetchQuery({
        queryKey: ['network-health'],
        queryFn: async () => {
          const response = await fetch(`/api/network-health`)
          return response.json()
        },
      }),
      // Prefetch validators for this network
      queryClient.prefetchQuery({
        queryKey: ['validators', network],
        queryFn: async () => {
          const module = await import('@/services/centralizedValidatorService')
          return module.default.getAllValidators()
        },
      }),
      // Prefetch vote accounts for this network
      queryClient.prefetchQuery({
        queryKey: ['vote-accounts', network],
        queryFn: async () => {
          const response = await fetch(`/api/rpc/vote-accounts?network=${network}`)
          if (!response.ok) throw new Error('Failed to fetch vote accounts')
          return response.json()
        },
      }),
      // Prefetch cluster nodes for this network (extract .nodes to match Validators page format)
      queryClient.prefetchQuery({
        queryKey: ['cluster-nodes', network],
        queryFn: async () => {
          const response = await fetch(`/api/rpc/cluster-nodes?network=${network}`)
          if (!response.ok) throw new Error('Failed to fetch cluster nodes')
          const data = await response.json()
          return data.nodes || []
        },
      }),
      // Prefetch staking yield for this network
      queryClient.prefetchQuery({
        queryKey: ['staking-yield', network],
        queryFn: async () => {
          const response = await fetch(`/api/staking/yield?network=${network}`)
          if (!response.ok) throw new Error('Failed to fetch staking yield')
          return response.json()
        },
      }),
    ])

    log.info(`Cache warmed for network: ${network}`)
  } catch (error) {
    log.warn(`Failed to warm cache for network ${network}:`, error)
  }
}
